import { TBaseItem } from "@centricsoftware/design-system";
import { ClientErrorFieldMap } from "csi/components/types";
import { ICache } from "csi/typings";
import { isDefined, isNullOrUndefined } from "csi/utility/coreUtility";
import { smoothScrollTop } from "csi/utility/htmlUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import { uniqueId } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import React, { createContext, RefObject, useContext } from "react";
import { updateFormState, validateAndSaveFormState } from "src/c8-platform/service/formService";
import { IForm, IFormField } from "src/c8-platform/type/form";
import { C8Node } from "src/c8-platform/type/node";
import { C8FieldValue, cnl } from "src/c8-platform/type/value";
import { focusOnFirstInputField, focusOnFirstInvalidInputField } from "./formUtility";

type ProcessForm = (formNodeCNL: cnl, bNavigate: boolean, bRepeat: boolean) => void
type CancelFormDialog = VoidFunction;

export type FormFieldOnChangeCallback = (e: React.SyntheticEvent<Element>, field: IFormField, value?: C8FieldValue, noUpdate?: boolean) => void;

export class FormStore extends ReactionStore {
	/** cache */
	readonly cache: ICache;

	/** callback to close the form */
	readonly cancelFormCallback: CancelFormDialog;

	/** indicate if this form supports Save and Go */
	readonly canNavigate: boolean;

	/** indicate if this form supports Save and New */
	readonly canRepeat: boolean;

	/** client side error fields */
	@observable clientErrorFieldMap: ClientErrorFieldMap = {};

	/** form error message */
	@observable formErrorMessage: string;

	/** form state data */
	@observable formInfo: IForm;

	/** form node */
	@observable.ref formNode: C8Node;

	/** form ref */
	formRef: RefObject<HTMLFormElement>;

	/** indicate if needs to go to the newly created node */
	navigateToNewNode: boolean = false;

	/** callback to process the form */
	readonly processFormCallback: ProcessForm;

	/** render key */
	renderKey: string = uniqueId();

	/** indicate if needs to continue creating nodes */
	repeat: boolean;

	/** support nodes related to the form */
	@observable.ref supportNodes: C8Node[];

	/** is form error, first check the form error message, then check the client side error fields, then check the fields with invalid state */
	checkFormError(formInfo: IForm): boolean {
		return !!this.formErrorMessage || Object.keys(this.clientErrorFieldMap).length > 0 || formInfo.fields.some((field) => field.transientInfo.invalid);
	}

	/** get whether any field or client field has an error */
	get hasFieldError(): boolean {
		return Object.keys(this.clientErrorFieldMap).length > 0 || this.formInfo.fields.some((field) => field.transientInfo.invalid);
	}

	/** Computed map of fields by id for fast lookup */
	@computed private get fieldsById(): Map<string, IFormField> {
		const map = new Map<string, IFormField>();
		this.formInfo.fields.forEach((field) => {
			map.set(field.id, field);
		});
		return map;
	}

	/** set client side error field */
	@action setClientErrorField = (fieldId: string, errorMessage: string): void => {
		if (errorMessage) {
			this.clientErrorFieldMap[fieldId] = errorMessage;
		} else {
			delete this.clientErrorFieldMap[fieldId];
		}
	};

	// get value based on value type
	// TODO: support more value types
	getValue = (e: React.SyntheticEvent<Element>, value: C8FieldValue): C8FieldValue => {
		if (e) {
			value = (e.currentTarget as HTMLInputElement).value;
		}
		return value;
	};

	/** return options for single select and multi select form fields */
	getFieldOptions = (attributeId: string, isSingleSelect: boolean): TBaseItem[] => {
		const field = this.formInfo.fields.find((field) => field.attributeId === attributeId);
		const { transientInfo: { displayValue, value } } = field;
		if (isSingleSelect) {
			// construct options for showing stored info
			return displayValue ? [{
				id: value,
				name: displayValue,
			} as TBaseItem] : [];
		} else {
			// construct options for showing stored info
			const displayOptions = displayValue as string[];
			if (displayOptions?.length > 0) {
				return displayOptions.map((option, index) => ({
					id: value[index],
					name: displayValue[index],
				} as TBaseItem));
			}
		}
		return [];
	};

	/** get field error */
	getFieldError = (fieldId: string): [boolean, string] => {
		const field = this.fieldsById.get(fieldId);
		const clientErrorField = this.clientErrorFieldMap[fieldId];
		const { invalid, message } = field.transientInfo;
		return [invalid || !!clientErrorField, message || clientErrorField];
	};

	/** handle field change, calling onFieldChange after getting the value based on value type */
	handleFieldChange = (e: React.SyntheticEvent<Element>, field: IFormField, value?: C8FieldValue, noUpdate?: boolean): void => {
		let val = this.getValue(e, value);

		if (isNullOrUndefined(val)) {
			val = xml.attributeTypeGetDefaultValue(field.valueType, field.format);
		}

		if (isDefined(val)) {
			this.onFieldChange(field, val, noUpdate);
		}
	};

	/** on field change, get field info from ref form element, reset the form error message */
	@action onFieldChange = (field: IFormField, value: C8FieldValue, noUpdate: boolean): void => {
		const formField = this.fieldsById.get(field.id);
		if (formField) {
			formField.transientInfo.value = xml.attributeTypeIsNumeric(field.valueType) ? Number(value) : value;
			if (!noUpdate) {
				this.updateForm(formField);
			}
		}
	};

	/** update the form, handle error */
	@action updateForm = async (field: IFormField): Promise<void> => {
		const response = await updateFormState(this.formInfo, field.id);

		if (!response) {
			console.error(`Error updating the form with nodetype ${this.formInfo.nodeType}.`);
			return;
		}

		runInAction(() => {
			this.formInfo = response;
		});
	};

	/** cancel the form */
	onCancel = (): void => {
		this.cancelFormCallback();
	};

	/** set the flag to go to the new node */
	onSaveAndGo = (): void => {
		this.navigateToNewNode = true;
		this.onValidate();
	};

	/** set the flag to continue creating process */
	onSaveAndNew = (): void => {
		this.repeat = true;
		this.onValidate();
	};

	/** call operation to validate and save the form */
	onValidate = async (): Promise<boolean> => {
		const { cache, formInfo, formRef: { current }, navigateToNewNode, processFormCallback, repeat } = this;
		const formResponse = await validateAndSaveFormState(formInfo);

		const { errorMessage, formInfo: saveFormInfo, formNode, supportNodes } = formResponse;

		// need to check error message as well as isFormError because we just got the response
		const isFormError = this.checkFormError(saveFormInfo);

		if (!errorMessage && !isFormError) {
			// cache the nodes
			cache.add(formNode);
			supportNodes?.forEach((node) => cache.add(node));

			processFormCallback(formNode.$URL, navigateToNewNode, repeat);

			runInAction(() => {
				this.formNode = formNode;
				this.supportNodes = supportNodes;
				this.formErrorMessage = "";

				if (repeat) {
					this.formInfo = saveFormInfo;
					focusOnFirstInputField(current);
				}
			});

			this.repeat = false;

			return true;
		} else {
			runInAction(() => {
				this.formErrorMessage = errorMessage;
				this.formInfo = saveFormInfo;
			});
			if (this.hasFieldError) {
				focusOnFirstInvalidInputField(current);
			} else {
				smoothScrollTop(current);
			}
		}

		return false;
	};

	/** set form ref */
	setFormRef = (ref: RefObject<HTMLFormElement>): void => {
		this.formRef = ref;
	};

	constructor(
		cache: ICache,
		formInfo: IForm,
		processFormCallback: ProcessForm,
		cancelFormCallback: CancelFormDialog,
		canNavigate: boolean,
		canRepeat: boolean,
	) {
		super();
		makeObservable(this);
		this.cache = cache;
		this.formInfo = formInfo;
		this.processFormCallback = processFormCallback;
		this.cancelFormCallback = cancelFormCallback;
		this.canNavigate = canNavigate;
		this.canRepeat = canRepeat;
	}
}

export const FormStoreContext = createContext<FormStore>(undefined);
export const useFormStore = (): FormStore => useContext(FormStoreContext);